import { getAssetUrl } from 'lib/config/assetUrls';

/**
 * Festive gifting assets on the CDN (`public-web-assets.uh-static.com`).
 *
 * Rising-card glare needs a sharp plate + a progressive-blur plate as two files —
 * a single blur plate breaks the desktop dissolve. Lettering/mark are drawn live
 * (see FestiveRisingCard Engraving) — plates must be metal-only, no baked copy.
 *
 * Panel cutouts must be people-only (no baked “PERFORMANCE LAB / FESTIVE EDITION”
 * frames). New keys avoid the bad `*-halftone.png` Figma exports.
 *
 * Banner and icon keys carry `?v=` so a CDN miss cached before the upload
 * does not stick. CustomImage's loader keeps that query and appends `/w_`.
 *
 * People banners: `banners/people/{1..4}.png` (alpha + soft bottom blur).
 * Icons: `cards/card-icons/` (IV drips: `cards/icons/oct26/`). Stage locally in
 * `cdn-upload/web_v2/performance-lab/festive/` then sync to S3.
 */
export const FESTIVE_CDN_PREFIX = 'web_v2/performance-lab/festive';

/** Campaign T&Cs, linked under the gift page FAQ. */
export const FESTIVE_TERMS_URL = getAssetUrl(
  `/${FESTIVE_CDN_PREFIX}/UH%20T%26Cs%20-%20Circle%20of%20Care%20Gifted%20Forward%20(UH%20Oct%2023).docx`,
);

/** Bust CDN cache when banners/icons are re-uploaded. */
const FESTIVE_ASSET_REV = 'v=11';
/** Rising-card plates — bump when the hero metal is re-exported. */
const FESTIVE_CARD_REV = 'v=5';

/** Soft-blur people cutouts — same file for desk + mobile; CSS frames the crop. */
const peopleBanner = (n: 1 | 2 | 3 | 4) =>
  `${FESTIVE_CDN_PREFIX}/banners/people/${n}.png?${FESTIVE_ASSET_REV}`;

export const FESTIVE_CDN_ASSETS = {
  giftCard: `${FESTIVE_CDN_PREFIX}/hero/rising-card.webp?${FESTIVE_CARD_REV}`,
  giftCardSharp: `${FESTIVE_CDN_PREFIX}/hero/rising-card-sharp.webp?${FESTIVE_CARD_REV}`,
  heroMaskLines: `${FESTIVE_CDN_PREFIX}/hero/hero-mask-lines.svg`,
  longevityBanner: `${FESTIVE_CDN_PREFIX}/banners/longevity-woman-cutout.webp?v=2`,
  recoveryBanner: `${FESTIVE_CDN_PREFIX}/banners/recovery-man-cutout.webp?v=2`,
  /** 1 diagnostics · 2 membership · 3 recovery · 4 IV */
  diagnosticsBannerDesk: peopleBanner(1),
  diagnosticsBannerMobile: peopleBanner(1),
  membershipBannerDesk: peopleBanner(2),
  membershipBannerMobile: peopleBanner(2),
  recoveryBannerDesk: peopleBanner(3),
  recoveryBannerMobile: peopleBanner(3),
  ivBannerDesk: peopleBanner(4),
  ivBannerMobile: peopleBanner(4),
  ivHydromax: `${FESTIVE_CDN_PREFIX}/cards/iv/iv-hydromax.png`,
  ivVitaglow: `${FESTIVE_CDN_PREFIX}/cards/iv/iv-vitaglow.png`,
  ivMegaboost: `${FESTIVE_CDN_PREFIX}/cards/iv/iv-megaboost.png`,
  ivNad: `${FESTIVE_CDN_PREFIX}/cards/iv/iv-nad.png`,
  ivMiniboost: `${FESTIVE_CDN_PREFIX}/cards/iv/iv-miniboost.png`,
  /** Topographic lines behind the checkout gift card (left panel). */
  checkoutPanelLines: `${FESTIVE_CDN_PREFIX}/checkout/panel-lines.svg`,
  checkoutCheck: `${FESTIVE_CDN_PREFIX}/checkout/check.svg`,
  checkoutChevron: `${FESTIVE_CDN_PREFIX}/checkout/chevron-left.svg`,
} as const;

/** `folder` is the path under `cards/`: card-icons (Figma 5989:808) or icons/oct26 (IV). */
const cardIcon = (folder: string, id: string) =>
  `${FESTIVE_CDN_PREFIX}/cards/${folder}/${id}-4x.png?${FESTIVE_ASSET_REV}`;

export const FESTIVE_CARD_ICONS: Record<string, string> = {
  'diagnostics-basic': cardIcon('card-icons', 'diagnostics-basic'),
  'diagnostics-pro': cardIcon('card-icons', 'diagnostics-pro'),
  'diagnostics-elite': cardIcon('card-icons', 'diagnostics-elite'),
  'recovery-membership-monthly': cardIcon(
    'card-icons',
    'recovery-membership-monthly',
  ),
  'recovery-membership-annual': cardIcon(
    'card-icons',
    'recovery-membership-annual',
  ),
  cryotherapy: cardIcon('card-icons', 'cryotherapy'),
  hbot: cardIcon('card-icons', 'hbot'),
  'sports-massage': cardIcon('card-icons', 'sports-massage'),
  'recovery-passes': cardIcon('card-icons', 'recovery-passes'),
  'iv-hydromax': cardIcon('icons/oct26', 'iv-hydromax'),
  'iv-vitaglow': cardIcon('icons/oct26', 'iv-vitaglow'),
  'iv-megaboost': cardIcon('icons/oct26', 'iv-megaboost'),
  'iv-nad-125': cardIcon('icons/oct26', 'iv-nad-125'),
};
